import {
  View,
  Text,
  Pressable,
  Modal,
  ScrollView,
  Platform,
  type ViewStyle,
} from "react-native";
import { BlurView } from "expo-blur";
import { Info } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type InfoSection = {
  title?: string;
  body: string;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  title: string;
  sections: InfoSection[];
};

/**
 * Small (i) button for the top-right corner of cards.
 */
export function InfoButton({
  onPress,
  accessibilityLabel = "View more information",
  action,
  testID,
}: {
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `accessibilityLabel` is translated. */
  action?: string;
  testID?: string;
}) {
  const capturePress = usePressCapture("InfoButton");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onPress={() => {
        capturePress(pressLabel(accessibilityLabel, testID), action);
        onPress();
      }}
      hitSlop={10}
      style={{
        width: 28,
        height: 28,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 14,
        backgroundColor: "rgba(255, 255, 255, 0.25)",
      }}
    >
      <Info size={14} color={EMBER.colors.textFaint} strokeWidth={1.8} />
    </Pressable>
  );
}

function InfoContent({
  title,
  sections,
  onClose,
}: {
  title: string;
  sections: InfoSection[];
  onClose: () => void;
}) {
  const capturePress = usePressCapture("InfoSheet");
  return (
    <>
      {/* Close ✕ */}
      <Pressable
        onPress={() => {
          capturePress("close_info_sheet");
          onClose();
        }}
        hitSlop={12}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 28,
          height: 28,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontSize: 18,
            fontWeight: "400",
            color: EMBER.colors.textFaint,
          }}
        >
          {"\u2715"}
        </Text>
      </Pressable>

      {/* Title */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 18,
          fontWeight: "700",
          color: EMBER.colors.text,
          marginBottom: 20,
        }}
      >
        {title}
      </Text>

      {/* Sections */}
      <ScrollView
        style={{ maxHeight: 400 }}
        showsVerticalScrollIndicator={false}
      >
        {sections.map((section, i) => (
          <View
            key={i}
            style={{ marginBottom: i < sections.length - 1 ? 18 : 0 }}
          >
            {section.title && (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                  marginBottom: 4,
                }}
              >
                {section.title}
              </Text>
            )}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                lineHeight: 20,
              }}
            >
              {section.body}
            </Text>
          </View>
        ))}
      </ScrollView>
    </>
  );
}

export default function InfoSheet({
  visible,
  onClose,
  title,
  sections,
}: Props) {
  const capturePress = usePressCapture("InfoSheet");
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={() => {
          capturePress("dismiss_info_sheet_backdrop");
          onClose();
        }}
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "88%",
            maxWidth: 380,
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          {Platform.OS === "web" ? (
            <View
              style={
                {
                  paddingHorizontal: 24,
                  paddingTop: 24,
                  paddingBottom: 24,
                  backgroundColor: "rgba(255,255,255,0.88)",
                  backdropFilter: "blur(24px) saturate(1.4)",
                  WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                } as ViewStyle
              }
            >
              <InfoContent
                title={title}
                sections={sections}
                onClose={onClose}
              />
            </View>
          ) : (
            <BlurView
              intensity={80}
              tint="light"
              style={{
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 24,
              }}
            >
              <InfoContent
                title={title}
                sections={sections}
                onClose={onClose}
              />
            </BlurView>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
